import adapter from 'webrtc-adapter';
import { all } from 'async-saga';
import { SagaContext, takeEvery } from './types';
import Janus from '../../external/janus.es';
import { CallView, CreateEndpointView, EndpointView, LineView } from '../../mediator/janussession';
import { Call, Endpoint, Line, MediaTrackCapabilitiesEx, MediaTrackConstraintSetEx, MediaTrackSettingsEx, PtzFeature, PtzSetting } from '../../model';
import { selectCall, selectSettings } from '../reducers/janus';
import { setDefaultAudioConstraints } from '../../mediator/callstatemachine';
import { actions } from '../types';
import { CallStatusErrorCode } from '../../mediator/errorCodes';


let endpointView: EndpointView

function lineView(): LineView {
  return endpointView.lineView
}


function callView(index: number): CallView {
  return lineView().calls[index]
}

function getActiveCallView(): CallView {
  const lv = lineView()
  return lv.calls[lv.callIndex]
}

const onInit = takeEvery(actions.initApp, async (ctx) => {
  window.citrixVdaSupport ? console.log('Initialising Janus with Citrix') : console.log('Initialising Janus')
  Janus.init({
    debug: true,
    dependencies: Janus.useDefaultDependencies({ adapter }),
    callback: function () {
      endpointView = CreateEndpointView({
        onEndpointStatusChange() {
          // add the created call
          if (lineView() && !selectCall(ctx.getState())) {
            onCallStatusChanged()
          }

          ctx.dispatch(actions.endpointStatusChanged(getEndpoint()))

          if (getEndpoint().status === 'online') {
            onLineStatusChanged()
          }
        },
        onLineRequestRegisterFailed: onLineStatusChanged,
        onLineRequestUregisterFailed: onLineStatusChanged,
        onLineRegisterRequested: onLineStatusChanged,
        onLineUnregisterRequested: onLineStatusChanged,
        onLineRegisterStatusChange: onLineStatusChanged,
        onMwiStatusChange: onLineStatusChanged,
        onCallStatusChange(_sipserver: string, _username: string, _errorCode: CallStatusErrorCode, _errorMessage: string) {
          onCallStatusChanged()
        },
        onMediaStatusChange(_sipserver: string, _username: string) {
          onCallStatusChanged()
        },
        onStatsUpdated(_stats: RTCStatsReport) {
          onCallStatusChanged()
        },
        onSlowAudioUpLink(_slow: boolean) {
          onCallStatusChanged()
        },
        onSlowAudioDownLink(_slow: boolean) {
          onCallStatusChanged()
        },
        onHeadsetControlInitialised(control: EndpointView) {
          setTimeout(async () => {
            const devices = await control.GetWebHidDeviceList();
            console.log('Headset control initialised: WebHid device list: ', devices);
            ctx.dispatch(actions.setWebHidDeviceList(devices));  
          });
        },
      }, true, 'info')

      endpointView.init()


    },
  })

  function onLineStatusChanged() {
    const line = getLine()
    ctx.dispatch(actions.lineStatusChanged(line))
  }

  function onCallStatusChanged() {
    const lv = lineView()
    const calls = lv.calls as CallView[]
    const tsCalls = calls.map((_value, index) => getCall(index))
    ctx.dispatch(actions.callStatusChanged(lv.callIndex, tsCalls))
  }

  setDefaultAudioConstraints(ctx.getState().janus.mediaConstraint)
})

const connectEndpoint = takeEvery(actions.connectEndpoint, async (ctx, { extension, pin }) => {
  const settings = selectSettings(ctx.getState())

  //const Url = 'https://mobility-dev.ipfx.com:8089/janus'
  //const SipDomain = 'NZVOIP02_001'
  //const TurnServers = ['turn:mobility-dev.ipfx.com:3473?transport=udp','turn:mobility-dev.ipfx.com:3473?transport=tcp']
  let turnServers = []
  if (settings.turnServer != null && settings.turnServer.length > 0) {
    if (settings.turnServerUdpPort !== '') {
      turnServers.push('turn:' + settings.turnServer + ':' + settings.turnServerUdpPort + '?transport=udp')
    }

    if (settings.turnServerTcpPort !== '') {
      turnServers.push('turn:' + settings.turnServer + ':' + settings.turnServerTcpPort + '?transport=tcp')
    }
  }

  const turnUsername = settings.turnUsername ? settings.turnUsername : extension
  const turnPassword = settings.turnPassword ? settings.turnPassword : pin
  const sipRealm = settings.sipRealm ? settings.sipRealm : undefined // empty string, null and undefined --> undefined
  const serverUsername = settings.serverUsername ? settings.serverUsername : undefined // empty string, null and undefined --> undefined
  const serverPassword = settings.serverPassword ? settings.serverPassword : undefined // empty string, null and undefined --> undefined

  // 4 for director
  endpointView.doConnect(settings.janus, settings.sipDomain, extension, pin, turnServers, turnUsername, turnPassword, true, null, null, null, 4, sipRealm, serverUsername, serverPassword, null)
})

const connectEndpointGuest = takeEvery(actions.connectEndpointGuest, async (ctx, { guestId, guestPasskey, displayName }) => {
  const settings = selectSettings(ctx.getState())

  //const TurnServers = ['turn:mobility-dev.ipfx.com:3473?transport=udp','turn:mobility-dev.ipfx.com:3473?transport=tcp']
  let turnServers = []
  if (settings.turnServer != null && settings.turnServer.length > 0) {
    if (settings.turnServerUdpPort !== '') {
      turnServers.push('turn:' + settings.turnServer + ':' + settings.turnServerUdpPort + '?transport=udp')
    }

    if (settings.turnServerTcpPort !== '') {
      turnServers.push('turn:' + settings.turnServer + ':' + settings.turnServerTcpPort + '?transport=tcp')
    }
  }

  const sipRealm = settings.sipRealm ? settings.sipRealm : 'guest.ipfx.pbx'
  // 4 for director
  endpointView.doConnectGuest(settings.janus, turnServers, settings.sipDomain, guestId, guestPasskey, 4, sipRealm, displayName)
})

const disconnectEndpoint = takeEvery(actions.disconnectEndpoint, async (_ctx) => {
  endpointView.doDisconnect(undefined);
})

const pairHeadset = takeEvery(actions.pairHeadset, async (_ctx) => {
  const isWebHidEnabled = navigator.userAgent.indexOf('electron') !== -1;
  console.log("Action: pair headset, is enabled: ", isWebHidEnabled);
  // @ts-ignore
  if (isWebHidEnabled) {
    setTimeout(async () => {
      const deviceList = await endpointView.GetWebHidDeviceList();
      console.log("Web HID device list length: ", deviceList.length);
      _ctx.dispatch(actions.setWebHidDeviceList(deviceList))
      if (deviceList.length > 0) {
        const selectedDeviceId = deviceList[0].deviceId as string;
        const selectedDeviceName = deviceList[0].deviceName as string;
        console.log("Selecting device: ", selectedDeviceId, ", name ", selectedDeviceName);
        try {
          const webHidDeviceId = await endpointView.selectByWebHidDevice(selectedDeviceId);
          _ctx.dispatch(actions.setSelectedJabraDevice({ deviceName: selectedDeviceName, deviceId: selectedDeviceId }))

        } catch (error) {
          console.error("Failed to select webHID: ", error);
        }
      } else {
        console.info("No devices avalailbel to pair");
      }
    }, 2000);
  }
})



// const register = takeEvery(actions.register, async () => {
//   lineView().doRequestRegister()
// })

// const unregister = takeEvery(actions.unregister, async () => {
//   lineView().doRequestUnregister()
// })

const configureMediaConstraints = takeEvery(actions.configureMediaConstraints, async (_ctx, payload) => {
  setDefaultAudioConstraints(payload)
})

const dial = takeEvery(actions.dial, async (_ctx, payload) => {
  getActiveCallView().doMakeCall(payload)
})

const hangUp = takeEvery(actions.hangUp, async () => {
  getActiveCallView().doHangup()
})

const muteMicrophone = takeEvery(actions.muteMicrophone, async (_ctx, payload) => {
  getActiveCallView().doMuteMicrophone(payload)
})
const dtmf = takeEvery(actions.dtmf, async (_ctx, payload) => {
  getActiveCallView().doSendDtmf(payload)
})

const answer = takeEvery(actions.answer, async () => {
  getActiveCallView().doAnswer()
})

const hold = takeEvery(actions.hold, async () => {
  getActiveCallView().doHold()
})

const resume = takeEvery(actions.resume, async () => {
  getActiveCallView().doResume()
})

const toggleVideo = takeEvery(actions.toggleVideo, async (_ctx, payload) => {
  console.info('toggling video to', payload)
  getActiveCallView().doEnableVideo(payload.enable, payload.videoSource)
})

function setPtz(ctx: SagaContext, key: keyof (PtzFeature), payload: number) {
  const call = selectCall(ctx.getState())
  if (!call.ptz) {
    console.warn(`${key} feature is not available`)
    return
  }

  const settings = call.ptz[key]
  if (call.hasLocalVideo && settings && payload >= settings.min && payload <= settings.max) {
    try {
      const track = call.localStream?.getVideoTracks()[0]
      const constraintSet: MediaTrackConstraintSetEx = {
        [key]: payload,
      }
      track?.applyConstraints({
        advanced: [constraintSet],
      })
    } catch (ex) {
      console.warn(`failed to set ${key} to ${payload}`, ex)
    }
  }
}

const setVideoPan = takeEvery(actions.setVideoPan, async (ctx, payload) => {
  setPtz(ctx, 'pan', payload)
})

const setVideoTilt = takeEvery(actions.setVideoTilt, async (ctx, payload) => {
  setPtz(ctx, 'tilt', payload)
})

const setVideoZoom = takeEvery(actions.setVideoZoom, async (ctx, payload) => {
  setPtz(ctx, 'zoom', payload)
})

const transfer = takeEvery(actions.transfer, async () => {
  getActiveCallView().doXfer()
})

const lineStatusChanged = takeEvery(actions.lineStatusChanged, async (ctx, payload) => {
  const line = payload
  // the registration will automatically retry by entering 'registering' state. Wait for 30s and then perform the retry.
  // we don't want to do this when it is forbidden, as the credentials are incorrect.
  if (line.statusError === '403 Forbidden' && line.status === 'registering') {
    console.warn('Incorrect registration credentials. Disconnecting endpoint')
    ctx.dispatch(actions.disconnectEndpoint())
  }
})

const getCallStats = takeEvery(actions.getCallStats, async () => {
  getActiveCallView().doGetStats()
})

function getEndpoint(): Endpoint {
  const { canConnect, canDisconnect, status, statusError } = endpointView
  return { canConnect, canDisconnect, status: status as any, statusError }
}

function getLine(): Line {
  const { canRequestRegister, canRequestUnregister, registerStatus, registerStatusError, mwiStatus, username, displayName } = lineView()
  return {
    canRegister: canRequestRegister,
    canUnregister: canRequestUnregister,
    status: registerStatus as any,
    statusError: registerStatusError,
    mwiStatus,
    username,
    displayName: trimQuotes(displayName),
  }
}

function constructPtzCapability(videoTrack: MediaStreamTrack): PtzFeature | undefined {
  if (!videoTrack) return undefined

  const caps = videoTrack.getCapabilities() as MediaTrackCapabilitiesEx
  const settings = videoTrack.getSettings() as MediaTrackSettingsEx

  if (settings.pan !== undefined || settings.tilt !== undefined || settings.zoom !== undefined) {
    return {
      pan: caps.pan ? { ...caps.pan, current: settings.pan! } : undefined,
      tilt: caps.tilt ? { ...caps.tilt, current: settings.tilt! } : undefined,
      zoom: caps.zoom ? { ...caps.zoom, current: settings.zoom! } : undefined,
    }
  }

  return undefined
}

function getCall(index: number): Call {
  const {
    callId,
    state, statusErrorCode, statusErrorDescription, localStream, remoteStream,
    canMakeCall, canRingOut, canHangup, canAnswer, answerRequested,
    featureHoldResume, canHold, canResume, canBlindXfer, canXfer, canSendDtmf,
    isMuted, /*hasLocalVideo, hasRemoteVideo,*/ videoSource,
    peerUsername, peerDisplay, stats,
    canPlayBusyTone, canPlayReorderTone, canPlayNuTone, lastHangupCode,
    isSlowAudioDownlink, isSlowAudioUplink,
  } = callView(index)

  // we're overriding this because because
  const videoTrack: MediaStreamTrack = localStream?.getVideoTracks()?.length > 0 ? localStream.getVideoTracks()[0] : null
  const hasRemoteVideo = remoteStream?.getVideoTracks()?.length > 0 ?? false
  const hasLocalVideo = videoTrack != null
  const ptz = constructPtzCapability(videoTrack)

  return {
    callId,
    status: state as any,
    statusErrorCode: statusErrorCode as CallStatusErrorCode,
    statusErrorDescription: statusErrorDescription == null ? undefined : typeof statusErrorDescription === 'object' ? JSON.stringify(statusErrorDescription) : statusErrorDescription,
    localStream, remoteStream,

    canDial: canMakeCall, canRingOut, canHangUp: canHangup,
    canAnswer, answerRequested, featureHoldResume, canHold, canResume,
    canBlindXfer, canXfer, canSendDtmf,
    isMuted, hasRemoteVideo, hasLocalVideo, videoSource, ptz,
    peerUsername, peerDisplayName: trimQuotes(peerDisplay),
    canPlayBusyTone, canPlayReorderTone, canPlayNuTone, lastHangupCode,
    isSlowAudioDownlink, isSlowAudioUplink,
    stats: stats,
    isScreenSharing: () => hasLocalVideo && (videoSource === 'window' || videoSource === 'screen'),
  }
}

export default all(
  onInit,
  connectEndpoint,
  connectEndpointGuest,
  disconnectEndpoint,
  pairHeadset,
  // register,
  // unregister,
  configureMediaConstraints,
  dial,
  hangUp,
  muteMicrophone,
  dtmf,
  answer,
  hold,
  resume,
  toggleVideo,
  setVideoPan,
  setVideoTilt,
  setVideoZoom,
  transfer,
  lineStatusChanged,
  getCallStats,
)

const TrimQuotesRegExp = /^"|"$/g
function trimQuotes(peerDisplay: any): string {
  return String(peerDisplay).replace(TrimQuotesRegExp, '');
}
